import React, { useRef, useEffect, useState } from 'react';
import { Tabs, Tab, cn, Tooltip } from '@heroui/react';
import { motion } from 'framer-motion';
/**
 * 탭 네비게이션 컴포넌트 (HeroUI underlined 버전)
 *
 * 여러 탭을 포함하는 상단 네비게이션 컴포넌트입니다.
 * Home, Profile 등 다양한 페이지에서 재사용할 수 있습니다.
 *
 * HeroUI 공식 방식:
 * - variant="underlined"로 깔끔한 언더라인 스타일
 * - shadow 완전 제거, 언더라인 애니메이션만 유지
 * - 사용자 요청 스타일과 동일하게 커스터마이징
 */ const TabNav = ({ activeTabId, tabs, onTabChange, variant = 'border', className, iconOnly = false })=>{
    const tabListRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
    useEffect(()=>{
        const checkMobile = ()=>{
            const width = window.innerWidth;
            setWindowWidth(width);
            // 1280px 미만에서 아이콘 모드 활성화 (데스크톱 최소 너비 기준)
            setIsMobile(width < 1280);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return ()=>window.removeEventListener('resize', checkMobile);
    }, []);
    // 더 넓은 화면에서도 아이콘 모드 사용 (1400px 미만)
    const prefersIconOnly = iconOnly || isMobile || windowWidth < 1400;
    const onDrag = (_event, info)=>{
        if (tabListRef.current) {
            tabListRef.current.scrollLeft -= info.delta.x;
        }
    };
    // 숨김처리되지 않은 탭만 필터링
    const visibleTabs = tabs.filter((tab)=>!tab.isHidden);
    return /*#__PURE__*/ React.createElement("div", {
        className: cn("flex w-full flex-col", className)
    }, /*#__PURE__*/ React.createElement(motion.div, {
        drag: "x",
        onDrag: onDrag,
        dragConstraints: {
            left: 0,
            right: 0
        },
        style: {
            cursor: 'grab'
        }
    }, /*#__PURE__*/ React.createElement(Tabs, {
        "aria-label": "Navigation",
        selectedKey: activeTabId,
        onSelectionChange: (key)=>onTabChange(key),
        variant: "underlined",
        color: "primary",
        fullWidth: true,
        classNames: {
            tabList: cn('w-full relative rounded-none p-0 border-b', variant === 'border' ? 'border-gray-200/40' : 'border-gray-100/30', prefersIconOnly ? 'gap-2' : 'gap-6'),
            cursor: 'bg-primary-500',
            tab: cn('flex-1 h-12', prefersIconOnly ? 'px-1 min-w-[48px]' : 'px-0'),
            tabContent: 'text-foreground/70 group-data-[selected=true]:text-primary-600'
        }
    }, visibleTabs.map((tab)=>{
        const shouldShowIconOnly = prefersIconOnly && Boolean(tab.icon);
        const tabContent = /*#__PURE__*/ React.createElement("div", {
            className: cn('flex items-center justify-center', shouldShowIconOnly ? 'space-x-0' : 'space-x-2')
        }, tab.icon && /*#__PURE__*/ React.createElement("span", {
            className: cn(shouldShowIconOnly ? 'text-base' : '')
        }, tab.icon), (!shouldShowIconOnly || !tab.icon) && /*#__PURE__*/ React.createElement("span", {
            className: "whitespace-nowrap"
        }, tab.label));
        return /*#__PURE__*/ React.createElement(Tab, {
            key: tab.id,
            "data-testid": tab.dataTestId,
            title: shouldShowIconOnly ? /*#__PURE__*/ React.createElement(Tooltip, {
                content: tab.label,
                placement: "bottom",
                delay: 0,
                closeDelay: 0,
                className: "text-xs"
            }, tabContent) : tabContent
        });
    }))));
};
export default TabNav;
